import * as React from 'react';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { Grid, Box, Typography } from '@material-ui/core';
import { AnimatedItem } from '../components/animations/AnimatedItem';
import { MatterTerminology } from '../MatterTerminology';
import { browserHistory } from '../components/AppContent';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import LibraryAddCheckIcon from '@material-ui/icons/LibraryAddCheck';
import DeveloperModeOutlinedIcon from '@material-ui/icons/DeveloperModeOutlined';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import CommonMaterialCard from './CommonMaterialCard';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import { RoleConfigurationCard } from './RoleConfigurationCard';
import { UserFeature, UserFeatureEnum } from '../types/UserFeature';
import MailIcon from '@material-ui/icons/Mail';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined';
import TuneOutlinedIcon from '@material-ui/icons/TuneOutlined';
import SettingsInputSvideoIcon from '@material-ui/icons/SettingsInputSvideo';
import SecurityIcon from '@material-ui/icons/Security';
import StorageIcon from '@material-ui/icons/Storage';
import DashboardIcon from '@material-ui/icons/Dashboard';
import ForumIcon from '@material-ui/icons/Forum';
import BuildIcon from '@material-ui/icons/Build';
import { LearnMoreEnum } from '../learnMore/LearnMorePage';
import { RouterProps } from '../App';
import AppsOutlinedIcon from '@material-ui/icons/AppsOutlined';
import { TypeOfDashboard } from '../featureConfigurationLayout/FeatureLayoutConfigurationPage';
import BusinessCenterOutlinedIcon from '@material-ui/icons/BusinessCenterOutlined';
import AccessibilityNewOutlinedIcon from '@material-ui/icons/AccessibilityNewOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import TextFieldsIcon from '@material-ui/icons/TextFields';
import TimelineIcon from '@material-ui/icons/Timeline';
import CallSplitIcon from '@material-ui/icons/CallSplit';
import { PageContentLayout } from '../v2/layouts/PageContentLayout';

export enum ListOfConfigEnum {
    lookupConfiguration = 'lookupConfiguration',
    firmStructureConfiguration = 'firmStructureConfiguration',
    helpConfiguration = 'helpConfiguration',
    brandConfiguration = 'brandConfiguration',
    userConfiguration = 'userConfiguration',
    integrationConfiguration = 'integrationConfiguration',
    firmSettingConfiguration = 'firmSettingConfiguration',
    applicationSettings = 'applicationSettings',
    emailConfiguration = 'emailConfiguration',
    splashScreenConfiguration = 'splashScreenConfiguration',
    featureAccessConfiguration = 'featureAccessConfiguration',
    featureLicenceConfiguration = 'featureLicenceConfiguration',
    generalMaterialConfiguration = 'generalMaterialConfiguration',
    roleSecurityConfiguration = 'roleSecurityConfiguration',
    matterViewColumnSecurityConfiguration = 'matterViewColumnSecurityConfiguration',
    enquiryCustomFieldsConfiguration = 'enquiryCustomFieldsConfiguration',
    taskflowConfiguration = 'taskflowConfiguration',
}

interface ExternalProps {}

interface Data {
    loading?: boolean;
    networkStatus?: number;
    // tslint:disable-next-line: no-any
    appUser: any;
}

interface ConfigurationDashboardProps extends ExternalProps, RouterProps {
    data: Data;
}

interface ConfigurationDashboardState {
    search: string;
}


// tslint:disable-next-line:no-any
class ConfigurationDashboard extends React.Component<ConfigurationDashboardProps, ConfigurationDashboardState> {
    constructor(props: ConfigurationDashboardProps) {
        super(props);
        this.state = { search: '' };
        this.onClickBackBtn = this.onClickBackBtn.bind(this);
        this.onLearnMore = this.onLearnMore.bind(this);
        this.onClickDashboard = this.onClickDashboard.bind(this);
        this.onNavigateToConfig = this.onNavigateToConfig.bind(this);
        this.onDocument = this.onDocument.bind(this);
    }

    render() {
        const terminology = { ...MatterTerminology };

        const isLoading = !this.props.data || this.props.data.loading;
        const isInternalAdmin = isLoading ? false : this.props.data.appUser.isInternalAdmin;
        const { search } = this.state;
        const lowerSearch = search.toLowerCase();

        interface CardDef {
            show: boolean;
            title: string;
            description: string;
            element: React.ReactNode;
        }

        interface CardGroup {
            label: string;
            icon: React.ReactNode;
            iconColor: string;
            cards: CardDef[];
        }

        const cardGroups: CardGroup[] = [
            {
                label: 'Security & Access',
                icon: <SecurityIcon style={{ fontSize: 15 }} />,
                iconColor: '#1565c0',
                cards: [
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasFirmSettingsConfiguration) || isInternalAdmin,
                        title: 'Firm Settings',
                        description: 'Configure settings and defaults for the Firm',
                        element: (
                            <CommonMaterialCard
                                icon={<SettingsOutlinedIcon />}
                                title="Firm Settings"
                                description="Configure settings and defaults for the Firm"
                                onClick={this.onNavigateToConfig(ListOfConfigEnum.firmSettingConfiguration)}
                            />
                        ),
                    },
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasFeatureAccessConfiguration) || isInternalAdmin,
                        title: 'Feature Access',
                        description: 'Configure access to application features',
                        element: (
                            <CommonMaterialCard
                                icon={<LockOpenIcon />}
                                title="Feature Access"
                                description="Configure access to application features"
                                onClick={this.onNavigateToConfig(ListOfConfigEnum.featureAccessConfiguration)}
                                onDocument={() => this.onDocument(ListOfConfigEnum.featureAccessConfiguration)}
                            />
                        ),
                    },
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasRoleConfiguration) || isInternalAdmin,
                        title: 'Roles',
                        description: 'Configure the roles for users',
                        element: <RoleConfigurationCard />,
                    },
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasRoleSecurityConfiguration) || isInternalAdmin,
                        title: terminology.RoleSecurity,
                        description: 'Configure the matter information that different roles are allowed to access',
                        element: (
                            <CommonMaterialCard
                                icon={<AccessibilityNewOutlinedIcon />}
                                title={terminology.RoleSecurity}
                                description="Configure the matter information that different roles are allowed to access"
                                onClick={this.onNavigateToConfig(ListOfConfigEnum.roleSecurityConfiguration)}
                            />
                        ),
                    },
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasSummaryColumnSecurity),
                        title: 'Matter View Column Security',
                        description: 'Configure the columns in the matter summary view that users are allowed to access',
                        element: (
                            <CommonMaterialCard
                                icon={<VisibilityOutlinedIcon />}
                                title="Matter View Column Security"
                                description="Configure the columns in the matter summary view that users are allowed to access"
                                onClick={this.onNavigateToConfig(ListOfConfigEnum.matterViewColumnSecurityConfiguration)}
                            />
                        ),
                    },
                ],
            },
            {
                label: 'Structure & Data',
                icon: <StorageIcon style={{ fontSize: 15 }} />,
                iconColor: '#2e7d32',
                cards: [
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasFirmStructure) || isInternalAdmin,
                        title: 'Firm Structure',
                        description: 'Maintain information relating to staff groups and structures',
                        element: (
                            <CommonMaterialCard
                                icon={<AccountTreeIcon />}
                                title="Firm Structure"
                                description="Maintain information relating to staff groups and structures"
                                onClick={this.onNavigateToConfig(ListOfConfigEnum.firmStructureConfiguration)}
                                onLearnMore={() => this.onLearnMore(LearnMoreEnum.FIRM_STRUCTURE)}
                            />
                        ),
                    },
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasLookupConfiguration) || isInternalAdmin,
                        title: 'Lookup Lists',
                        description: 'Maintain various reference lookup information',
                        element: (
                            <CommonMaterialCard
                                icon={<LibraryAddCheckIcon />}
                                title="Lookup Lists"
                                description="Maintain various reference lookup information"
                                onClick={this.onNavigateToConfig(ListOfConfigEnum.lookupConfiguration)}
                                onLearnMore={() => this.onLearnMore(LearnMoreEnum.LOOKUP_LISTS)}
                            />
                        ),
                    },
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasEnquiryCustomFieldsConfiguration) || isInternalAdmin,
                        title: 'Enquiry Electronic Intake Forms',
                        description: 'Define and maintain custom intake forms for the electronic collection of enquiry data.',
                        element: (
                            <CommonMaterialCard
                                icon={<TextFieldsIcon />}
                                title="Enquiry Electronic Intake Forms"
                                description="Define and maintain custom intake forms for the electronic collection of enquiry data."
                                onClick={this.onNavigateToConfig(ListOfConfigEnum.enquiryCustomFieldsConfiguration)}
                            />
                        ),
                    },
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasBrandsConfiguration) || isInternalAdmin,
                        title: 'Brands',
                        description: 'Configuration of brand',
                        element: (
                            <CommonMaterialCard
                                icon={<AppsOutlinedIcon />}
                                title="Brands"
                                description="Configuration of brand"
                                onClick={this.onNavigateToConfig(ListOfConfigEnum.brandConfiguration)}
                            />
                        ),
                    },
                    {
                        show: isInternalAdmin,
                        title: 'Taskflow',
                        description: 'Build a guided task sequence for a matter category or type',
                        element: (
                            <CommonMaterialCard
                                icon={<CallSplitIcon />}
                                title="Taskflow"
                                description="Build a guided task sequence for a matter category or type"
                                onClick={this.onNavigateToConfig(ListOfConfigEnum.taskflowConfiguration)}
                            />
                        ),
                    },
                ],
            },
            {
                label: 'Dashboards',
                icon: <DashboardIcon style={{ fontSize: 15 }} />,
                iconColor: '#6a1b9a',
                cards: [
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasFirmDashboardConfiguration) || isInternalAdmin,
                        title: 'Firm Dashboard',
                        description: 'Configure the screen elements to be displayed in firm dashboards',
                        element: (
                            <CommonMaterialCard
                                icon={<TrendingUpIcon />}
                                title="Firm Dashboard"
                                description="Configure the screen elements to be displayed in firm dashboards"
                                onClick={() => this.onClickDashboard(TypeOfDashboard.firm)}
                            />
                        ),
                    },
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasFeeEarnerDashboardConfiguration) || isInternalAdmin,
                        title: terminology.FeeEarnerDashboard,
                        description: 'Configure the screen elements to be displayed in fee earner dashboard',
                        element: (
                            <CommonMaterialCard
                                icon={<TrendingUpIcon />}
                                title={terminology.FeeEarnerDashboard}
                                description="Configure the screen elements to be displayed in fee earner dashboard"
                                onClick={() => this.onClickDashboard(TypeOfDashboard.feeEarner)}
                            />
                        ),
                    },
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasLeadsDashboardConfiguration) || isInternalAdmin,
                        title: 'Marketing Dashboard',
                        description: 'Configure the screen elements to be displayed in marketing dashboard',
                        element: (
                            <CommonMaterialCard
                                icon={<TrendingUpIcon />}
                                title="Marketing Dashboard"
                                description="Configure the screen elements to be displayed in marketing dashboard"
                                onClick={() => this.onClickDashboard(TypeOfDashboard.lead)}
                            />
                        ),
                    },
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasTaskDashboardConfiguration) || isInternalAdmin,
                        title: 'Task Dashboard',
                        description: 'Configure the screen elements to be displayed in task dashboard',
                        element: (
                            <CommonMaterialCard
                                icon={<TrendingUpIcon />}
                                title="Task Dashboard"
                                description="Configure the screen elements to be displayed in task dashboard"
                                onClick={() => this.onClickDashboard(TypeOfDashboard.task)}
                            />
                        ),
                    },
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasClientDashboardConfiguration) || isInternalAdmin,
                        title: 'Client Dashboard',
                        description: 'Configure the screen elements to be displayed in client dashboard',
                        element: (
                            <CommonMaterialCard
                                icon={<TrendingUpIcon />}
                                title="Client Dashboard"
                                description="Configure the screen elements to be displayed in client dashboard"
                                onClick={() => this.onClickDashboard(TypeOfDashboard.client)}
                            />
                        ),
                    },
                ],
            },
            {
                label: 'Content & Comms',
                icon: <ForumIcon style={{ fontSize: 15 }} />,
                iconColor: '#e65100',
                cards: [
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasEmailTemplateConfigurationPage) || isInternalAdmin,
                        title: 'Email & Message Templates',
                        description: 'Configuration of email & SMS template layouts to be used by the system.',
                        element: (
                            <CommonMaterialCard
                                icon={<MailIcon />}
                                title="Email & Message Templates"
                                description="Configuration of email & SMS template layouts to be used by the system."
                                onClick={this.onNavigateToConfig(ListOfConfigEnum.emailConfiguration)}
                            />
                        ),
                    },
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasSplashScreenConfigurationPage) || isInternalAdmin,
                        title: 'Splash Templates',
                        description: 'Configuration of Splash Screens to appear at user login',
                        element: (
                            <CommonMaterialCard
                                icon={<DeveloperModeOutlinedIcon />}
                                title="Splash Templates"
                                description="Configuration of Splash Screens to appear at user login"
                                onClick={this.onNavigateToConfig(ListOfConfigEnum.splashScreenConfiguration)}
                            />
                        ),
                    },
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasUserDefinedHelpConfigurationPage) || isInternalAdmin,
                        title: 'User Defined Help',
                        description: 'Configuration of user defined help',
                        element: (
                            <CommonMaterialCard
                                icon={<HelpOutlineIcon />}
                                title="User Defined Help"
                                description="Configuration of user defined help"
                                onClick={this.onNavigateToConfig(ListOfConfigEnum.helpConfiguration)}
                            />
                        ),
                    },
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasFeatureLicencingConfiguration) || isInternalAdmin,
                        title: 'Knowledge Bank',
                        description: 'Configuration of knowledge bank',
                        element: (
                            <CommonMaterialCard
                                icon={<BusinessCenterOutlinedIcon />}
                                title="Knowledge Bank"
                                description="Configuration of knowledge bank"
                                onClick={this.onNavigateToConfig(ListOfConfigEnum.generalMaterialConfiguration)}
                            />
                        ),
                    },
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasIntegrationConfiguration) || isInternalAdmin,
                        title: 'Integrations',
                        description: 'Configuration of system integrations with third parties',
                        element: (
                            <CommonMaterialCard
                                icon={<SettingsInputSvideoIcon />}
                                title="Integrations"
                                description="Configuration of system integrations with third parties"
                                onClick={this.onNavigateToConfig(ListOfConfigEnum.integrationConfiguration)}
                            />
                        ),
                    },
                ],
            },
            {
                label: 'System',
                icon: <BuildIcon style={{ fontSize: 15 }} />,
                iconColor: '#f57c00',
                cards: [
                    {
                        show: UserFeature.HasAccess(UserFeatureEnum.hasFeatureLicencingConfiguration) || isInternalAdmin,
                        title: 'Feature Licensing',
                        description: 'Licence use of application features',
                        element: (
                            <CommonMaterialCard
                                icon={<VpnKeyIcon />}
                                title="Feature Licensing"
                                description="Licence use of application features"
                                onClick={this.onNavigateToConfig(ListOfConfigEnum.featureLicenceConfiguration)}
                            />
                        ),
                    },
                    {
                        show: isInternalAdmin,
                        title: 'Application Settings',
                        description: 'Configure internal settings used by the application',
                        element: (
                            <CommonMaterialCard
                                icon={<SettingsOutlinedIcon />}
                                title="Application Settings"
                                description="Configure internal settings used by the application"
                                onClick={this.onNavigateToConfig(ListOfConfigEnum.applicationSettings)}
                            />
                        ),
                    },
                ],
            },
        ];

        return (
            <PageContentLayout
                pageHeader={{
                    icon: <TuneOutlinedIcon fontSize="small" />,
                    title: terminology.Configuration,
                    subtitle: 'Manage system settings and integrations',
                    onLearnMore: () => this.onLearnMore(LearnMoreEnum.System_Configuration_Overview),
                }}
                search={search}
                onSearchChange={s => this.setState({ search: s })}
                searchPlaceholder="Search configuration…"
            >
                <Box>
                    {cardGroups.map(group => {
                        const visibleGroupCards = group.cards.filter(card =>
                            card.show && (
                                !lowerSearch ||
                                card.title.toLowerCase().includes(lowerSearch) ||
                                card.description.toLowerCase().includes(lowerSearch)
                            )
                        );

                        if (visibleGroupCards.length === 0) { return null; }

                        return (
                            <Box key={group.label} mb={3}>
                                <div className="rv-config-section-heading">
                                    <span className="rv-config-section-heading-icon" style={{ color: group.iconColor }}>
                                        {group.icon}
                                    </span>
                                    <Typography variant="overline">
                                        {group.label}
                                    </Typography>
                                </div>
                                <Grid container={true} spacing={1} className="rv-5col-grid">
                                    {visibleGroupCards.map((card, index) => (
                                        <Grid key={index} item={true} xs={12} sm={6} md={4} lg={3}>
                                            <AnimatedItem delay={index * 10}>
                                                {card.element}
                                            </AnimatedItem>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        );
                    })}
                </Box>
            </PageContentLayout>
        );
    }

    private onClickBackBtn() {
        // reserved
    }

    private onClickDashboard(dashboardType: TypeOfDashboard) {
        sessionStorage.setItem('personalise', '');
        let layoutId = 0;
        switch (dashboardType) {
            case TypeOfDashboard.firm:        layoutId = 0; break;
            case TypeOfDashboard.lead:        layoutId = 1; break;
            case TypeOfDashboard.feeEarner:   layoutId = 2; break;
            case TypeOfDashboard.task:        layoutId = 3; break;
            case TypeOfDashboard.client:      layoutId = 4; break;
            default:                          layoutId = 0; break;
        }
        browserHistory.push({
            pathname: '/dashboardLayoutConfiguration',
            state: { layoutId },
        });
    }

    private onNavigateToConfig = (navigate: ListOfConfigEnum) => () => {
        browserHistory.push({ pathname: `/${ListOfConfigEnum[navigate]}` });
    }

    private onLearnMore(type: LearnMoreEnum) {
        this.props.history.push({ pathname: `/learnMore/${LearnMoreEnum[type]}` });
    }

    private onDocument(navigate: ListOfConfigEnum) {
        let link = '';
        if (navigate === ListOfConfigEnum.featureAccessConfiguration) {
            link = 'https://redraincorp.sharepoint.com/:b:/s/RedViewProduct/EZ1FKuPeYgZGuCcOGwV2tmIB4mGdOmIIPSP0gRWryuy_NA?e=yQgMJ4';
        }
        window.open(link, '_blank');
    }
}

export const UserData = gql`
    query UserData {
        appUser {
            isInternalAdmin
        }
    }
`;

// tslint:disable-next-line:no-any
export const ConfigurationDashboardPage = graphql<any, ExternalProps, any>(UserData, {
    options: () => ({ variables: {}, fetchPolicy: 'cache-first' }),
})(ConfigurationDashboard);
